import Box, { BoxProps } from '@mui/material/Box'
import { FC, memo, PropsWithChildren, useCallback, useEffect, useRef } from 'react'
import useStableCallback from '../../../hooks/shared/useStableCallback'

const useIntersectionObserver = ({
  onIntersecting,
  onNotIntersecting,
  threshold,
  root,
  rootMargin,
}: {
  onIntersecting?: (args: {
    observer: IntersectionObserver
    entry: IntersectionObserverEntry
  }) => void
  onNotIntersecting?: (args: {
    observer: IntersectionObserver
    entry: IntersectionObserverEntry
  }) => void
  threshold?: number | number[]
  root?: React.RefObject<Element> | null
  rootMargin?: string
}) => {
  const intersectionObserverRef = useRef<IntersectionObserver | null>(null)
  const stableOnIntersecting = useStableCallback(
    ({ observer, entry }: { observer: IntersectionObserver; entry: IntersectionObserverEntry }) => {
      if (onIntersecting) {
        onIntersecting({
          observer,
          entry,
        })
      }
    },
  )
  const stableOnNotIntersecting = useStableCallback(
    ({ observer, entry }: { observer: IntersectionObserver; entry: IntersectionObserverEntry }) => {
      if (onNotIntersecting) {
        onNotIntersecting({
          observer,
          entry,
        })
      }
    },
  )
  const setTarget = useCallback(
    (node: Element | null) => {
      // disconnect previous target
      intersectionObserverRef.current?.disconnect()
      if (!node) return
      const intersectionObserver = new IntersectionObserver(
        (entries, intersectionObserver) => {
          const entry = entries[0]
          if (!entry) return
          if (entry.isIntersecting) {
            stableOnIntersecting({ observer: intersectionObserver, entry })
          } else {
            stableOnNotIntersecting({ observer: intersectionObserver, entry })
          }
        },
        { root: root?.current, rootMargin, threshold },
      )
      intersectionObserver.observe(node)
      intersectionObserverRef.current = intersectionObserver
    },
    [stableOnIntersecting, stableOnNotIntersecting, root, rootMargin, threshold],
  )
  useEffect(() => () => intersectionObserverRef.current?.disconnect(), [])
  return setTarget // callback ref for the sentinel
}
const InfiniteScroll: FC<
  PropsWithChildren &
    BoxProps & {
      loadMore: () => void
      scrollContainerRef?: React.RefObject<Element>
    }
> = ({ loadMore, children, scrollContainerRef, ...rest }) => {
  const setSentinel = useIntersectionObserver({
    onIntersecting: loadMore,
    threshold: 1,
    root: scrollContainerRef,
  })
  return (
    <Box {...rest}>
      {children}
      <Box ref={setSentinel} style={{ height: 1 }} />
    </Box>
  )
}
export default memo(InfiniteScroll)
